// Three-path picker for the OMR sheet-music import:
//
//   1. Pick a full PDF the user has already uploaded to their library.
//      Resolves the score's PDF as a File via loadScorePDF + getScoreMetadata.
//
//   2. Pick a rectangular EXCERPT of a score from the library. Opens the
//      score picker, then ScoreExcerptPicker for drag-to-crop selection.
//      Yields a high-resolution PNG of just the chosen region.
//
//   3. Upload a fresh PDF from disk. Not saved to the library — strictly
//      a one-shot OMR import.
//
// On any path, the resolved File (PDF or PNG) is handed to onPickFile(file)
// and the modal closes. The caller handles the actual upload + import.

import React, { useRef, useState } from "react";
import { FileText, Crop, Upload, X } from "lucide-react";
import ScorePicker from "../scores/ScorePicker";
import ScoreExcerptPicker from "./ScoreExcerptPicker";
import {
  getScoreMetadata,
  loadScorePDF,
} from "../../utils/scoreStorage";

async function loadScoreAsPdfFile(userId, scoreId) {
  const meta = await getScoreMetadata(userId, scoreId);
  if (!meta || !meta.storagePath) {
    throw new Error("Score is missing its storage path. Try re-uploading it.");
  }
  const blobUrl = await loadScorePDF(userId, scoreId, meta.storagePath);
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    const safeName = (meta.title || "score").replace(/[^\w\-. ]+/g, "_");
    return new File([blob], `${safeName}.pdf`, { type: "application/pdf" });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export default function ImportSheetMusicModal({ userId, onPickFile, onClose }) {
  // chooser            — initial three-button screen.
  // picker-full        — ScorePicker; selecting a score loads its full PDF.
  // picker-excerpt     — ScorePicker; selecting a score opens excerpt picker.
  // excerpt            — ScoreExcerptPicker on the chosen score.
  const [view, setView] = useState("chooser");
  const [excerptScore, setExcerptScore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  // ScorePicker.handleSelect fires onSelect AND onClose synchronously, so
  // for the excerpt flow (where we need the dialog to stay mounted while we
  // transition to ScoreExcerptPicker) we swallow exactly one onClose after a
  // successful pick. Cancellation via the X button still closes normally.
  const swallowNextClose = useRef(false);

  if (view === "picker-full") {
    return (
      <ScorePicker
        userId={userId}
        onSelect={async (scoreId) => {
          if (busy) return;
          setBusy(true);
          setError(null);
          try {
            const file = await loadScoreAsPdfFile(userId, scoreId);
            onPickFile(file);
            onClose();
          } catch (err) {
            console.error("Could not load score PDF:", err);
            setError(err.message || "Could not load that score's PDF.");
            setBusy(false);
            setView("chooser");
          }
        }}
        onClose={onClose}
      />
    );
  }

  if (view === "picker-excerpt") {
    return (
      <ScorePicker
        userId={userId}
        onSelect={(scoreId, scoreTitle) => {
          // The picker hands back (id, title); the excerpt picker also wants
          // basic metadata so it can title the cropped file.
          swallowNextClose.current = true;
          setExcerptScore({ id: scoreId, title: scoreTitle });
          setView("excerpt");
        }}
        onClose={() => {
          if (swallowNextClose.current) {
            swallowNextClose.current = false;
            return;
          }
          onClose();
        }}
      />
    );
  }

  if (view === "excerpt" && excerptScore) {
    return (
      <ScoreExcerptPicker
        userId={userId}
        score={excerptScore}
        onExcerptSelected={(file) => {
          onPickFile(file);
          onClose();
        }}
        onClose={onClose}
      />
    );
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    onPickFile(file);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Import sheet music"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-lg font-semibold">Import Sheet Music</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {error && (
            <div className="rounded border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">
              {error}
            </div>
          )}

          <p className="text-sm text-gray-600">
            FlowFrame will read the sheet music and try to generate a
            click/drone/tempo timeline.
          </p>

          <button
            type="button"
            onClick={() => {
              setError(null);
              setView("picker-full");
            }}
            className="w-full flex items-start gap-3 px-4 py-3 border rounded hover:bg-gray-50 text-left"
          >
            <FileText className="mt-0.5 text-blue-600" size={22} />
            <span className="flex-1">
              <span className="block font-medium">Choose from your scores</span>
              <span className="block text-sm text-gray-600">
                Run OMR on an entire PDF you've already uploaded to your library.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setError(null);
              setView("picker-excerpt");
            }}
            className="w-full flex items-start gap-3 px-4 py-3 border rounded hover:bg-gray-50 text-left"
          >
            <Crop className="mt-0.5 text-blue-600" size={22} />
            <span className="flex-1">
              <span className="block font-medium">Choose excerpt from your scores</span>
              <span className="block text-sm text-gray-600">
                Pick a score and drag a rectangle around just the measures
                you want. Often more accurate than running OMR on a whole page.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-start gap-3 px-4 py-3 border rounded hover:bg-gray-50 text-left"
          >
            <Upload className="mt-0.5 text-blue-600" size={22} />
            <span className="flex-1">
              <span className="block font-medium">Upload a new PDF</span>
              <span className="block text-sm text-gray-600">
                Use a PDF from your computer. Not saved to your library.
              </span>
            </span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </div>
  );
}
